import {
  apiCollectionStoreAdd,
  apiCollectionStoreCancel,
  apiCollectionStoreStatus,
  apiCommodityWriteOffCommodityStoreDetail,
  ApiCommodityWriteOffCommodityStoreDetailResponse,
  apiEvaluationEvaluateStoreList,
  ApiEvaluationEvaluateStoreListResponse,
} from '@/api'
import { AsyncReturnType, RequiredDeep } from 'vtils/types'
import { createModelPlus, prepareData, requiredDeep, submit } from '@/utils'
import { dcb } from '@dcb/jssdk'
import { defineProvider } from '@/hooks'
import { Ref } from '@vue/composition-api'

export interface RawBookModel
  extends RequiredDeep<ApiCommodityWriteOffCommodityStoreDetailResponse> {
  /** 店铺ID */
  martId: string

  /** 状态文案 */
  statusText: string

  /** 是否休息中 */
  isRest: boolean

  /** 是否已停业 */
  isClosed: boolean

  /** 是否已收藏 */
  isFavorite: boolean

  /** 评价数量 */
  commentCount: number

  /** 评价列表 */
  commentList: RequiredDeep<ApiEvaluationEvaluateStoreListResponse>['list']

  /** 收藏/取消收藏 */
  toggleFavorite: () => any
}

export type BookModel = AsyncReturnType<typeof getBookModel>

export async function getBookModel(martId: string) {
  const { longitude, latitude } = await dcb
    .getLocation()
    .catch(() => ({} as AsyncReturnType<typeof dcb['getLocation']>))

  const { martData, commentData, favoriteData } = await prepareData({
    martData: apiCommodityWriteOffCommodityStoreDetail({
      storeId: martId,
      longitude: longitude as any,
      latitude: latitude as any,
    }) as any as Promise<
      RequiredDeep<ApiCommodityWriteOffCommodityStoreDetailResponse>
    >,
    commentData: apiEvaluationEvaluateStoreList({
      storeId: martId,
      havingPicFlag: 0,
      pageIndex: 1,
      pageSize: 2,
    }) as any as Promise<RequiredDeep<ApiEvaluationEvaluateStoreListResponse>>,
    favoriteData: apiCollectionStoreStatus({
      storeId: martId,
    }).then(requiredDeep),
  })

  const bookModel = createModelPlus<RawBookModel>({
    ...{},
    ...martData,
    get martId() {
      return martId
    },
    get statusText() {
      return this.store.storeStatus === 1
        ? '待上线'
        : this.store.storeStatus === 2
        ? '营业中'
        : this.store.storeStatus === 3
        ? '已停业'
        : this.store.storeStatus === 4
        ? '休息中'
        : '未知'
    },
    get isRest() {
      return this.store.storeStatus === 4
    },
    get isClosed() {
      return this.store.storeStatus === 3
    },
    isFavorite: favoriteData.status === 1,
    get commentCount() {
      return commentData.total
    },
    get commentList() {
      return commentData.list
    },
    async toggleFavorite() {
      await submit(async $ => {
        $.start()
        if (this.isFavorite) {
          await apiCollectionStoreCancel({
            storeIds: [martId],
          })
        } else {
          await apiCollectionStoreAdd({
            storeId: martId,
          })
        }
        const res = await apiCollectionStoreStatus({
          storeId: martId,
        })
        this.isFavorite = res.status === 1
        await $.success(this.isFavorite ? '收藏成功' : '取消收藏成功')
      })
    },
  })

  return bookModel
}

export const bookModelProvider = defineProvider<Ref<BookModel>>()
